import { Component, OnInit } from '@angular/core';
import { ClaimRow } from '../../../../models/api/view/claim-view/claim-row';
import {
  Accomm,
  AccommTypeDetails,
} from '../../../../models/api/view/claim/details/accommodations/accommodation-details';
import { BaseComponent } from '../../../base/base.component';
import { LinkLabelComponent } from '../../../core/labels/link-label.component';
import { PageHeaderComponent } from '../../../core/labels/page-header.component';
import { CardComponent } from '../../../core/panels/card.component';
import { DetailItemComponent } from '../../../core/panels/detail-panel/detail-item.component';
import { DetailPanelComponent } from '../../../core/panels/detail-panel/detail-panel.component';
import { ColumnComponent } from '../../../core/table/column.component';
import { TableComponent } from '../../../core/table/table.component';

@Component({
  selector: 'app-accommodations',
  imports: [
    TableComponent,
    ColumnComponent,
    DetailPanelComponent,
    CardComponent,
    DetailItemComponent,
    PageHeaderComponent,
    LinkLabelComponent,
  ],
  template: `
    <app-page-header label="Accommodations" />

    <div class="tw-w-full tw-mt-2 tw-mb-5 tw-italic">
      If you are an authorized decision maker, job functions and claims that are
      pending business review can be approved/rejected in mySedgwick.
    </div>

    <app-table
      [dataSource]="accommodationData?.accommDetails || []"
      [pageSize]="5"
      [scrollable]="false"
      [expandable]="true"
      dataKey="accommodationId"
      recordLabel="accommodation"
      [showFilter]="true"
      [showFilterButton]="true"
      [globalFilterFields]="[
        'type',
        'subType',
        'beginDate',
        'endDate',
        'decision',
        'decisionDate',
        'leaveCaseNumber',
      ]"
    >
      <app-column name="type" label="TYPE" />
      <app-column name="subType" label="SUB TYPE" />
      <app-column name="beginDate" label="BEGIN DATE" format="d" />
      <app-column name="endDate" label="END DATE" format="d" />
      <app-column name="decision" label="DECISION" />
      <app-column name="decisionDate" label="DECISION DATE" format="d" />
      <app-column label="LEAVE CASE NUMBER" sortName="leaveCaseNumber">
        <ng-template let-data>
          <app-link-label
            (click)="
              navigateToLeaveClaim(data.leaveCaseID, data.leaveCaseNumber)
            "
          >
            {{ data.leaveCaseNumber }}
          </app-link-label>
        </ng-template>
      </app-column>
      <ng-template #expand let-data>
        <div class="tw-w-full tw-flex tw-gap-8">
          <div class="tw-w-1/3">
            <app-detail-panel
              class="tw-w-1/2"
              [dataSource]="getDetailedData(data.accommodationId)"
            >
              <app-detail-item name="description" label="Description" />
              <app-detail-item name="decisionMaker" label="Decision maker" />
              <app-detail-item
                name="firstDateOfAbsence"
                label="First date of absence"
                format="d"
              />
              @if (data.isLeaveOfAbsence) {
                <app-detail-item
                  name="stdConcurrent"
                  label="STD concurrent"
                  format="b"
                  labelCase="none"
                />
                <app-detail-item
                  name="workersCompConcurrent"
                  label="Workers' comp concurrent"
                  format="b"
                  labelCase="none"
                />
              }
            </app-detail-panel>
          </div>
          <div class="tw-w-1/3">
            <app-detail-panel
              [dataSource]="getDetailedData(data.accommodationId)"
            >
              <app-detail-item
                name="decisionDescription"
                label="Decision description"
              />
              <app-detail-item name="longTerm" label="Long term" />
              <app-detail-item
                name="leaveEndDate"
                label="Leave end date"
                format="d"
              />
              <app-detail-item
                name="expectedRtw"
                label="Expected RTW"
                format="d"
                labelCase="none"
              />
              @if (data.isLeaveOfAbsence) {
                <app-detail-item
                  name="ltdConcurrent"
                  label="LTD concurrent"
                  format="b"
                  labelCase="none"
                />
              }
            </app-detail-panel>
          </div>
          <div class="tw-w-1/3">
            <app-detail-panel
              [dataSource]="getDetailedData(data.accommodationId)"
            >
              <app-detail-item name="requestor" label="Requester">
                <ng-template #value let-data>
                  <ul style="list-style-type: disc; padding-left: 20px;">
                    @if (data.requestor && data.requestor.includes(',')) {
                      @for (req of data.requestor.split(','); track req) {
                        <li class="tw-mb-1">{{ req.trim() }}</li>
                      }
                    } @else {
                      <li class="tw-mb-1">{{ data.requestor }}</li>
                    }
                  </ul>
                </ng-template>
              </app-detail-item>
            </app-detail-panel>
          </div>
        </div>
        @if (data.isLeaveOfAbsence) {
          <div class="tw-full tw-flex tw-mt-4 tw-gap-4 tw-pb-8">
            <app-card class="tw-w-1/3" label="INTERMITTENT">
              <app-detail-panel
                [dataSource]="getDetailedData(data.accommodationId)"
              >
                <app-detail-item name="frequency" label="Frequency">
                  <ng-template #value let-detailData>
                    {{ formatFrequency(detailData.frequency) }}
                  </ng-template>
                </app-detail-item>
                <app-detail-item
                  name="episodePer"
                  label="Episode(s) Per"
                  labelCase="none"
                />
                <app-detail-item name="duration" label="Duration" />
              </app-detail-panel>
            </app-card>
            <app-card class="tw-w-1/3" label="TREATMENT">
              <app-detail-panel
                [dataSource]="getDetailedData(data.accommodationId)"
              >
                <app-detail-item name="tFrequency" label="Frequency">
                  <ng-template #value let-detailData>
                    {{ formatFrequency(detailData.tFrequency) }}
                  </ng-template>
                </app-detail-item>
                <app-detail-item
                  name="tEpisodePer"
                  label="Episode(s) Per"
                  labelCase="none"
                />
                <app-detail-item name="tDuration" label="Duration" />
              </app-detail-panel>
            </app-card>
          </div>
        }
      </ng-template>
    </app-table>
  `,
  styles: ``,
})
export class AccommodationsComponent extends BaseComponent implements OnInit {
  claimRow: ClaimRow;
  accommodationData: Accomm | null = null;

  getDetailedData(accommodationId: number): AccommTypeDetails {
    return this.accommodationData?.accommTypeDetails[accommodationId] || {};
  }

  formatFrequency(frequency?: number): string {
    if (frequency == null) return '';
    return frequency === 1 ? '1 episode' : `${frequency} episodes`;
  }

  async navigateToLeaveClaim(recordId: number, leaveCaseNumber: string) {
    const searchInput: any = {
      claimNumber: leaveCaseNumber,
      searchOptions: [],
      claimStatus: [],
      claimSubstatus: [],
      lob: [],
      claimType: [],
      caseType: [],
      claimFlags: [],
      ExactXRefMatchFlag: true,
    };

    const searchResult = await this.api.search.post(searchInput);

    // If we found a single result, navigate directly to it
    if (searchResult?.rows?.length === 1) {
      const foundClaim = searchResult.rows[0];
      const claimRow: any = {
        recordId: foundClaim.recordId,
        recordSource: foundClaim.recordSource,
      };

      await this.view.navigateClaim(claimRow);
      return;
    }

    const quickSearchOption = {
      label: `Claim/Event number begins with <b>${leaveCaseNumber}</b>`,
      type: 'claim-event-number',
      value: leaveCaseNumber,
    };

    this.router.navigate(['/view'], {
      queryParams: {
        tab: 'search',
        quicksearch: JSON.stringify(quickSearchOption),
      },
      queryParamsHandling: 'merge',
    });
  }

  async ngOnInit(): Promise<void> {
    this.claimRow = this.view.currentClaim();

    this.accommodationData = await this.api.accommodationDetail.get({
      recordId: this.claimRow.recordId,
      recordSource: this.claimRow.recordSource,
    });

    if (this.accommodationData?.accommDetails) {
      this.accommodationData.accommDetails
        .sort((a, b) => {
          if (!a.beginDate && !b.beginDate) return 0;
          if (!a.beginDate) return 1;
          if (!b.beginDate) return -1;
          return (
            new Date(a.beginDate).getTime() - new Date(b.beginDate).getTime()
          );
        })
        .forEach((item) => {
          (item as any).isLeaveOfAbsence =
            item.type?.toLowerCase() === 'leave of absence';
        });
    }

    this.view.hideLoading();
  }
}
